import { Theme, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Box, FlexColBox, Icon, Label } from "../../../common/components";
import { createStyles, palette, spacing } from "../../../styles/theme";
import {
  DesignFamily,
  DesignProduct,
  Product,
  ProductLineType,
} from "../../../types";
import { ProductLinesToColor } from "../../../constant/common.constant";
import { isNaN } from "lodash";
import { useSpecs } from "../../../context/Specs/useSpecs";
import { useCurrency } from "../../../common/hooks/useCurrency";
import { getConditionnalStyle } from "../../../utils/themeUtils";
import { useAuth } from "../../../common/hooks/useAuth";

const useStyles = (theme: Theme) =>
  createStyles({
    container: {
      pt: { xs: spacing.smallMobile, lg: spacing.smallDesktop },
      pb: { xs: spacing.regularMobile, lg: spacing.regularDesktop },
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      width: "100%",
      cursor: "pointer",
      minWidth: "165px",
      maxWidth: "214px",
      [theme.breakpoints.down("sm")]: {
        px: 2,
      },
      borderRadius: "12px",
      border: `2px solid transparent`,
      "&:hover": {
        border: `2px solid ${palette.grey.lightGrey}`,
      },
      ":active": {
        border: `2px solid ${palette.brand.secondary}`,
      },
    },
    disabledInUS: {
      opacity: 0.5,
    },
    itemContainer: {
      position: "relative",
      "&>img": {
        minWidth: "150px",
        maxWidth: "150px",
        maxHeight: "150px",
        verticalAlign: "middle",
      },
    },
    disableOrDeprecatedTag: {
      position: "absolute",
      textWrap: "nowrap",
      top: "50%",
      left: "50%",
      transform: "translateX(-50%) translateY(-50%)",
      backgroundColor: palette.grey.lightGrey,
      padding: "4px 8px",
      borderRadius: "32px",
      textAlign: "center",
      border: "1px solid white",
      fontSize: "10px",
      fontWeight: 600,
      color: palette.grey.primary,
    },
    itemDescriptionContainer: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
    },
  });

interface Props {
  product: Product;
  onCreateDesign: (
    family: DesignFamily,
    product: DesignProduct,
    labelTag: string
  ) => void;
  dataTest: string;
  isFiltering: boolean;
}

export const ProductModalItem = (props: Props) => {
  const {
    product: {
      imgName,
      comingSoon,
      labelTag,
      family,
      product,
      minQty,
      line,
      deprecated,
    },
    onCreateDesign,
    dataTest,
  } = props;

  const { t } = useTranslation();
  const theme = useTheme();
  const { isAdmin } = useAuth();
  const styles = useStyles(theme);
  const { getProductSize, getProductDeliveryTime } = useSpecs();
  const { isUSD } = useCurrency();

  const disabledInUS = isUSD() && line === ProductLineType.CLASSIC;
  const isDeprecatedOrDisabled = deprecated || disabledInUS;

  const productSize = getProductSize(product);
  const productDeliveryTime = getProductDeliveryTime(product);

  const formatSize = (min?: string, max?: string) => {
    if (min === "OneSize" || max === "OneSize") {
      return t(`sizes.OneSize`);
    }
    if (!isNaN(Number(min)) && !isNaN(Number(max))) {
      return `${min}-${max} ${t("sizes.years")}`;
    }

    const minSize = t(`sizes.${min}`).split("-")[0];
    const maxSizeSplit = t(`sizes.${max}`).split("-");
    const maxSize = maxSizeSplit[maxSizeSplit.length - 1];

    return `${minSize}-${maxSize}`;
  };

  return (
    <Box
      data-test={dataTest}
      sx={[
        styles.container,
        isDeprecatedOrDisabled && !isAdmin
          ? { cursor: "not-allowed", pointerEvents: "none" }
          : null,
        //  comingSoon ? null : styles.containerSelectable
      ]}
      onClick={
        (!comingSoon && !isDeprecatedOrDisabled) || isAdmin
          ? () => onCreateDesign(family, product, t(labelTag))
          : undefined
      }
    >
      <Box sx={styles.itemContainer}>
        <img
          src={imgName}
          alt={t(labelTag)}
          style={isDeprecatedOrDisabled ? { opacity: 0.3 } : undefined}
        />
        {isDeprecatedOrDisabled && (
          <Box sx={styles.disableOrDeprecatedTag}>
            <Typography variant="textXs" color={palette.grey.primary}>
              {disabledInUS
                ? t("modal.select_canvas.not_available_in_us")
                : t("modal.select_canvas.deprecated")}
            </Typography>
          </Box>
        )}
      </Box>
      <Box
        sx={[
          styles.itemDescriptionContainer,
          getConditionnalStyle(disabledInUS, styles.disabledInUS),
        ]}
        gap={{ xs: spacing.smallMobile, lg: spacing.smallDesktop }}
      >
        <FlexColBox gap={"2px"} alignItems={"center"}>
          <Typography
            textAlign="center"
            variant="h5"
            color={isDeprecatedOrDisabled ? palette.grey.darkerGrey : undefined}
          >
            {t(labelTag)}
          </Typography>
          {isAdmin && (
            <Typography
              fontSize={10}
              lineHeight="normal"
              color={
                isDeprecatedOrDisabled
                  ? palette.grey.darkerGrey
                  : ProductLinesToColor[line]
              }
              fontWeight={700}
            >
              {t(`line.${line}.title`)}
            </Typography>
          )}
          {comingSoon && (
            <Label sx={{ textAlign: "center", fontSize: 12 }}>
              {t("modal.select_canvas.coming")}
            </Label>
          )}
        </FlexColBox>
        <Box display={"flex"} gap={`3px`} alignItems={"center"}>
          <Typography
            variant="textXs"
            color={
              isDeprecatedOrDisabled
                ? palette.grey.darkerGrey
                : palette.grey.primary
            }
          >
            {formatSize(
              productSize?.[0].name,
              productSize?.[productSize.length - 1].name
            )}
          </Typography>
          <Icon icon="circle" filled color={palette.grey.darkGrey} size={3} />
          <Typography
            variant="textXs"
            color={
              isDeprecatedOrDisabled
                ? palette.grey.darkerGrey
                : palette.grey.primary
            }
          >
            {t("modal.select_canvas.delivery_weeks_range", {
              range: `${productDeliveryTime?.minWeeks}-${productDeliveryTime?.maxWeeks}`,
            })}
          </Typography>
          <Icon
            icon="circle"
            filled
            color={
              isDeprecatedOrDisabled
                ? palette.grey.darkerGrey
                : palette.grey.darkGrey
            }
            size={3}
          />
          <Typography
            variant="textXs"
            color={
              isDeprecatedOrDisabled
                ? palette.grey.darkerGrey
                : palette.grey.primary
            }
          >
            {t("modal.select_canvas.min_qty", { minQty: minQty })}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
